import type { MedplumClient } from '@medplum/core';
import type { CarePlan, Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useMedplum } from '@eluve/feature-patients';
import {
  CARE_PLAN_GENERATION_JOB_IDENTIFIER_SYSTEM,
  ELUVE_FHIR_NAMESPACE,
} from '@eluve/plan-shapes';
import { useIsPostHogFeatureFlagEnabled } from '@eluve/posthog-react';

// Matches the backend's `buildIdentifierSystem('care-plan')` — kept in sync via
// the shared ELUVE_FHIR_NAMESPACE constant (the frontend can't import the
// nestjs-medplum helper). The draft CarePlan's identifier value is the
// appointment id.
const CARE_PLAN_IDENTIFIER_SYSTEM = `${ELUVE_FHIR_NAMESPACE}/care-plan-id`;

const POLL_INTERVAL_MS = 5_000;
// Stop polling after this window. We only poll once the appointment is completed
// (when the backend enqueues the job), and the Task is upserted `in-progress`
// within seconds, then finalized once the matcher runs. If no Task has even
// appeared by the end of this window the job never wrote one (e.g. it died
// before the upsert, or the visit predates this feature), so an uncapped 5s loop
// would run for as long as the view stays open. A fresh mount or `refetch()`
// opens a new window. (Interim bound until the @medplum/react `useSubscription`
// push path below lands.)
const MAX_POLL_DURATION_MS = 3 * 60_000;
const MAX_POLL_ATTEMPTS = Math.ceil(MAX_POLL_DURATION_MS / POLL_INTERVAL_MS);

/**
 * Derived lifecycle of the end-of-encounter draft (ENG-4706), read off the job
 * `Task` + the draft `CarePlan`. The Task is the source of truth: it only exists
 * once the job has been queued (post appointment-completion), so its absence
 * means nothing is happening and the UI stays inert.
 * - `idle`: Medplum off, the appointment hasn't completed, or no Task exists
 *   (no job was ever queued) — the hook renders nothing.
 * - `generating`: the job is running (Task `in-progress`, or `completed` with a
 *   focus whose CarePlan we haven't fetched yet) — "Eluve is preparing a care
 *   plan…".
 * - `ready`: the draft CarePlan exists — show the tab/callout.
 * - `none`: the matcher ran and quiet-skipped (Task `completed`, no focus) —
 *   "no care plan recommended".
 * - `failed`: the job threw (Task `failed`) — "couldn't prepare a care plan".
 */
export type CarePlanDraftState =
  | 'idle'
  | 'generating'
  | 'ready'
  | 'none'
  | 'failed';

type Signal = { carePlan: CarePlan | null; task: Task | null };

// MedplumClient caches GET/search responses in-memory keyed by URL (60s in the
// browser). Without bypassing it, the first poll — which runs before the draft
// exists — caches an empty result, and every poll for the next minute returns
// that stale empty, so a CarePlan/Task that lands seconds later is invisible
// until the cache expires or a fresh client is built (a page refresh). Polling
// must read through to the server every tick.
const NO_CACHE = { cache: 'no-cache' } as const;

async function fetchSignal(
  client: MedplumClient,
  appointmentId: string,
): Promise<Signal> {
  const [carePlan, task] = await Promise.all([
    client.searchOne(
      'CarePlan',
      { identifier: `${CARE_PLAN_IDENTIFIER_SYSTEM}|${appointmentId}` },
      NO_CACHE,
    ),
    client.searchOne(
      'Task',
      {
        identifier: `${CARE_PLAN_GENERATION_JOB_IDENTIFIER_SYSTEM}|${appointmentId}`,
      },
      NO_CACHE,
    ),
  ]);
  return { carePlan: carePlan ?? null, task: task ?? null };
}

// Map the (CarePlan, Task) pair to the derived state. The CarePlan is
// authoritative for `ready`; the Task carries the in-flight + terminal signal.
// No Task → no job was queued → `idle` (render nothing); the optimistic
// "assume generating" default is exactly the bug this avoids.
function deriveState(signal: Signal): CarePlanDraftState {
  if (signal.carePlan) {
    return 'ready';
  }
  const status = signal.task?.status;
  if (!status) {
    return 'idle';
  }
  if (status === 'failed') {
    return 'failed';
  }
  if (status === 'completed') {
    // Completed with no focus → quiet-skip ("no plan recommended"). With a
    // focus the draft exists but our CarePlan fetch hasn't surfaced it yet —
    // keep waiting (the next tick resolves it to `ready`).
    return signal.task?.focus?.reference ? 'generating' : 'none';
  }
  // Task `in-progress` → still preparing.
  return 'generating';
}

// States we stop polling on — the draft landed, the matcher quiet-skipped, or
// the job failed. `idle` (waiting for the Task to appear) and `generating` keep
// the loop alive.
function isTerminal(state: CarePlanDraftState): boolean {
  return state === 'ready' || state === 'none' || state === 'failed';
}

/**
 * Detects + tracks the end-of-encounter draft CarePlan for an appointment
 * (ENG-4699/ENG-4706). Once the appointment is `completed` — the lifecycle
 * moment the backend enqueues the job — polls Medplum for the job `Task` and the
 * draft `CarePlan`, exposing a derived {@link CarePlanDraftState}. The Task is
 * the source of truth: while it doesn't exist the hook stays `idle` (no job was
 * queued), so the UI never shows "preparing…" for a visit that hasn't completed.
 * Stops on any terminal state, and stops after MAX_POLL_DURATION_MS so a Task
 * that never appears doesn't poll forever. Inert unless the PostHog
 * `CARE_PLANS` product flag, `useMedplum().enabled`, and `completed` are all
 * true.
 *
 * `completed` gates *when* to poll, never *what* to render — the Task alone
 * decides the state. (It becomes moot once the `useSubscription` path lands.)
 *
 * TODO(jesse)[ENG-4699]: layer @medplum/react `useSubscription`
 * (`Task?encounter=<id>`) on top of this polling fallback once WebSocket
 * subscriptions are verified against the local Medplum server.
 */
export function useAppointmentCarePlanDraft(
  appointmentId: string,
  completed: boolean,
): {
  carePlan: CarePlan | null;
  state: CarePlanDraftState;
  refetch: () => void;
} {
  const { enabled, client } = useMedplum();
  const isCarePlansEnabled = useIsPostHogFeatureFlagEnabled('CARE_PLANS');
  // Poll only once the product and Medplum are on, a client exists, and the
  // appointment completed (the backend enqueues `generate_care_plan` in
  // `on_appointment_completed`).
  const active = isCarePlansEnabled && enabled && Boolean(client) && completed;
  const [carePlan, setCarePlan] = useState<CarePlan | null>(null);
  const [state, setState] = useState<CarePlanDraftState>('idle');
  // Once a terminal state is reached the poll loop stops itself without
  // re-subscribing the interval on every tick.
  const terminalRef = useRef(false);
  // Bounds the poll loop so a Task that never appears doesn't poll forever.
  const attemptsRef = useRef(0);
  // Bumped whenever the watched appointment/activation changes; an in-flight
  // fetch whose generation is stale must not write state (it would clobber the
  // newer appointment with the previous patient's draft/Task).
  const generationRef = useRef(0);

  const load = useCallback(async () => {
    if (!client) {
      return;
    }
    const generation = generationRef.current;
    try {
      const signal = await fetchSignal(client, appointmentId);
      if (generation !== generationRef.current) {
        // A newer appointment/load superseded this fetch — drop the result.
        return;
      }
      // Always reflect the fetched plan (null when absent) — a later poll that
      // finds no CarePlan must clear a previously-drafted one, or navigating from
      // an appointment with a draft to one without keeps rendering the stale plan.
      setCarePlan(signal.carePlan);
      const next = deriveState(signal);
      setState(next);
      if (isTerminal(next)) {
        terminalRef.current = true;
      }
    } catch {
      // Transient FHIR errors are non-fatal — the next poll retries.
    }
  }, [client, appointmentId]);

  const refetch = useCallback(() => {
    if (active) {
      void load();
    }
  }, [active, load]);

  useEffect(() => {
    // Invalidate any in-flight load from the previous appointment/activation.
    generationRef.current += 1;
    if (!active) {
      setCarePlan(null);
      setState('idle');
      terminalRef.current = false;
      attemptsRef.current = 0;
      return;
    }
    terminalRef.current = false;
    attemptsRef.current = 0;
    setState('idle');
    void load();
    const interval = setInterval(() => {
      attemptsRef.current += 1;
      if (terminalRef.current) {
        clearInterval(interval);
        return;
      }
      if (attemptsRef.current >= MAX_POLL_ATTEMPTS) {
        // Window lapsed with no terminal Task — stop. The last derived state
        // stands (`idle` if no Task ever appeared); we don't fabricate `none`,
        // which means "the matcher ran and declined".
        clearInterval(interval);
        terminalRef.current = true;
        return;
      }
      void load();
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [active, load]);

  return { carePlan, state, refetch };
}
